<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- Left Sidebar Navigation -->
    <MemberPortalNavigation />

    <!-- Main Content -->
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18"></h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/member-dashboard">Dashboard</router-link></li>
                    <li class="breadcrumb-item active"></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading your coverage details...</p>
          </div>

          <!-- Coverage Content -->
          <div v-else>
            <!-- Coverage Status Banner -->
            <div class="row">
              <div class="col-12">
                <div class="card coverage-status-card" :class="getCoverageStatusClass()">
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <div class="d-flex align-items-center mb-3">
                          <div class="coverage-icon me-3">
                            <i class="bx" :class="getCoverageStatusIcon()"></i>
                          </div>
                          <div>
                            <h4 class="text-white mb-1">{{ getCoverageStatusTitle() }}</h4>
                            <p class="text-white-75 mb-0">{{ getCoverageStatusMessage() }}</p>
                          </div>
                        </div>
                        <div class="d-flex flex-wrap gap-3 mt-3">
                          <div class="coverage-stat">
                            <span class="stat-label">Member ID</span>
                            <span class="stat-value">{{ profile.cbhi_number }}</span>
                          </div>
                          <div class="coverage-stat">
                            <span class="stat-label">Valid From</span>
                            <span class="stat-value">{{ formatDate(profile.registration_date) }}</span>
                          </div>
                          <div class="coverage-stat">
                            <span class="stat-label">Valid Until</span>
                            <span class="stat-value">{{ formatDate(profile.expiry_date) }}</span>
                          </div>
                          <div class="coverage-stat">
                            <span class="stat-label">Days Remaining</span>
                            <span class="stat-value" :class="daysUntilExpiry <= 30 ? 'text-warning' : ''">
                              {{ daysUntilExpiry }} days
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4 text-center text-md-end mt-3 mt-md-0">
                        <div class="coverage-badge">
                          <span class="badge-label">Annual Premium</span>
                          <span class="badge-amount">ETB {{ formatCurrency(profile.annual_premium) }}</span>
                        </div>
                        <router-link v-if="remainingBalance > 0" to="/payment-make" class="btn btn-light btn-sm mt-2">
                          <i class="bx bx-credit-card me-1"></i>Pay Now
                        </router-link>
                        <span v-else class="badge bg-white text-success mt-2 px-3 py-2">
                          <i class="bx bx-check-circle me-1"></i>Fully Paid
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Content Row -->
            <div class="row">
              <!-- Left Column - Plan Details -->
              <div class="col-xl-8">
                <!-- Plan Information Card -->
                <div class="card">
                  <div class="card-header bg-transparent">
                    <div class="d-flex justify-content-between align-items-center">
                      <h5 class="card-title mb-0">
                        <i class="bx bx-shield-check text-primary me-2"></i>Your Health Plan
                      </h5>
                      <span class="badge" :class="getPlanTypeBadgeClass(planInfo.plan_type)">
                        {{ planInfo.plan_type ? planInfo.plan_type.toUpperCase() : 'BASIC' }}
                      </span>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="plan-header mb-4">
                      <h4 class="plan-name">{{ planInfo.plan_name || 'CBHI Standard Plan' }}</h4>
                      <p class="text-muted mb-0">{{ planInfo.plan_description || 'Comprehensive health coverage for you and your family through the Community-Based Health Insurance scheme.' }}</p>
                    </div>

                    <!-- Plan Highlights -->
                    <div class="row g-3 mb-4">
                      <div class="col-md-4">
                        <div class="highlight-box">
                          <div class="highlight-icon bg-soft-primary">
                            <i class="bx bx-wallet text-primary"></i>
                          </div>
                          <div class="highlight-content">
                            <span class="highlight-label">Primary Premium</span>
                            <span class="highlight-value">ETB {{ formatCurrency(planInfo.primary_annual_amount) }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="highlight-box">
                          <div class="highlight-icon bg-soft-info">
                            <i class="bx bx-user-plus text-info"></i>
                          </div>
                          <div class="highlight-content">
                            <span class="highlight-label">Dependent Premium</span>
                            <span class="highlight-value">
                              {{ planInfo.dependent_annual_amount > 0 ? 'ETB ' + formatCurrency(planInfo.dependent_annual_amount) : 'Free' }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="highlight-box">
                          <div class="highlight-icon bg-soft-success">
                            <i class="bx bx-group text-success"></i>
                          </div>
                          <div class="highlight-content">
                            <span class="highlight-label">Free Dependents</span>
                            <span class="highlight-value">
                              {{ planInfo.max_free_dependents >= 999 ? 'Unlimited' : planInfo.max_free_dependents || 0 }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Coverage Limits -->
                    <div class="coverage-limits mb-4" v-if="planInfo.annual_coverage_limit || planInfo.per_visit_limit">
                      <h6 class="section-subtitle">
                        <i class="bx bx-trending-up me-2"></i>Coverage Limits
                      </h6>
                      <div class="row g-3">
                        <div class="col-md-6" v-if="planInfo.annual_coverage_limit">
                          <div class="limit-card">
                            <div class="d-flex justify-content-between align-items-center">
                              <span class="limit-label">Annual Coverage Limit</span>
                              <span class="limit-value text-primary">ETB {{ formatCurrency(planInfo.annual_coverage_limit) }}</span>
                            </div>
                            <div class="progress mt-2" style="height: 6px;">
                              <div class="progress-bar bg-primary" :style="{ width: getCoverageUsagePercent() + '%' }"></div>
                            </div>
                            <small class="text-muted">{{ getCoverageUsagePercent() }}% used this year</small>
                          </div>
                        </div>
                        <div class="col-md-6" v-if="planInfo.per_visit_limit">
                          <div class="limit-card">
                            <div class="d-flex justify-content-between align-items-center">
                              <span class="limit-label">Per Visit Limit</span>
                              <span class="limit-value text-info">ETB {{ formatCurrency(planInfo.per_visit_limit) }}</span>
                            </div>
                            <small class="text-muted">Maximum claimable per hospital visit</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Eligibility -->
                    <div class="eligibility-section mb-4" v-if="planInfo.eligibility_text">
                      <h6 class="section-subtitle">
                        <i class="bx bx-check-shield me-2"></i>Eligibility
                      </h6>
                      <p class="text-muted mb-0">{{ planInfo.eligibility_text }}</p>
                    </div>
                  </div>
                </div>

                <!-- Covered Services Card -->
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-list-check text-primary me-2"></i>Covered Services
                    </h5>
                  </div>
                  <div class="card-body">
                    <!-- Service Categories -->
                    <div class="services-grid">
                      <!-- Outpatient Services -->
                      <div class="service-category">
                        <div class="category-header">
                          <div class="category-icon bg-soft-primary">
                            <i class="bx bx-clinic text-primary"></i>
                          </div>
                          <h6 class="category-title">Outpatient Services</h6>
                        </div>
                        <ul class="service-list">
                          <li><i class="bx bx-check text-success"></i>General consultations</li>
                          <li><i class="bx bx-check text-success"></i>Laboratory tests & diagnostics</li>
                          <li><i class="bx bx-check text-success"></i>X-rays & imaging</li>
                          <li><i class="bx bx-check text-success"></i>Minor procedures</li>
                          <li><i class="bx bx-check text-success"></i>Prescription medications</li>
                        </ul>
                      </div>

                      <!-- Inpatient Services -->
                      <div class="service-category">
                        <div class="category-header">
                          <div class="category-icon bg-soft-info">
                            <i class="bx bx-bed text-info"></i>
                          </div>
                          <h6 class="category-title">Inpatient Services</h6>
                        </div>
                        <ul class="service-list">
                          <li><i class="bx bx-check text-success"></i>Hospital admission</li>
                          <li><i class="bx bx-check text-success"></i>Ward accommodation</li>
                          <li><i class="bx bx-check text-success"></i>Surgical procedures</li>
                          <li><i class="bx bx-check text-success"></i>Post-operative care</li>
                          <li><i class="bx bx-check text-success"></i>In-hospital medications</li>
                        </ul>
                      </div>

                      <!-- Emergency Services -->
                      <div class="service-category">
                        <div class="category-header">
                          <div class="category-icon bg-soft-danger">
                            <i class="bx bx-plus-medical text-danger"></i>
                          </div>
                          <h6 class="category-title">Emergency Services</h6>
                        </div>
                        <ul class="service-list">
                          <li><i class="bx bx-check text-success"></i>Emergency room visits</li>
                          <li><i class="bx bx-check text-success"></i>Ambulance services</li>
                          <li><i class="bx bx-check text-success"></i>Emergency surgery</li>
                          <li><i class="bx bx-check text-success"></i>Intensive care</li>
                          <li><i class="bx bx-check text-success"></i>Stabilization treatment</li>
                        </ul>
                      </div>

                      <!-- Maternal & Child Health -->
                      <div class="service-category">
                        <div class="category-header">
                          <div class="category-icon bg-soft-warning">
                            <i class="bx bx-heart text-warning"></i>
                          </div>
                          <h6 class="category-title">Maternal & Child Health</h6>
                        </div>
                        <ul class="service-list">
                          <li><i class="bx bx-check text-success"></i>Antenatal care (ANC)</li>
                          <li><i class="bx bx-check text-success"></i>Delivery services</li>
                          <li><i class="bx bx-check text-success"></i>Postnatal care (PNC)</li>
                          <li><i class="bx bx-check text-success"></i>Child immunizations</li>
                          <li><i class="bx bx-check text-success"></i>Growth monitoring</li>
                        </ul>
                      </div>

                      <!-- Preventive Care -->
                      <div class="service-category">
                        <div class="category-header">
                          <div class="category-icon bg-soft-success">
                            <i class="bx bx-shield-plus text-success"></i>
                          </div>
                          <h6 class="category-title">Preventive Care</h6>
                        </div>
                        <ul class="service-list">
                          <li><i class="bx bx-check text-success"></i>Health screenings</li>
                          <li><i class="bx bx-check text-success"></i>Vaccinations</li>
                          <li><i class="bx bx-check text-success"></i>Health education</li>
                          <li><i class="bx bx-check text-success"></i>Nutrition counseling</li>
                          <li><i class="bx bx-check text-success"></i>Family planning</li>
                        </ul>
                      </div>

                      <!-- Chronic Disease Management -->
                      <div class="service-category">
                        <div class="category-header">
                          <div class="category-icon bg-soft-secondary">
                            <i class="bx bx-pulse text-secondary"></i>
                          </div>
                          <h6 class="category-title">Chronic Conditions</h6>
                        </div>
                        <ul class="service-list">
                          <li><i class="bx bx-check text-success"></i>Diabetes management</li>
                          <li><i class="bx bx-check text-success"></i>Hypertension care</li>
                          <li><i class="bx bx-check text-success"></i>HIV/AIDS treatment</li>
                          <li><i class="bx bx-check text-success"></i>TB treatment</li>
                          <li><i class="bx bx-check text-success"></i>Regular follow-ups</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Exclusions Card -->
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-x-circle text-danger me-2"></i>Exclusions & Limitations
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="alert alert-light border mb-4">
                      <i class="bx bx-info-circle me-2"></i>
                      The following services are generally not covered under the CBHI scheme. Please review carefully.
                    </div>

                    <div class="row">
                      <div class="col-md-6">
                        <h6 class="exclusion-title text-danger">
                          <i class="bx bx-x me-1"></i>Not Covered
                        </h6>
                        <ul class="exclusion-list">
                          <li>Cosmetic surgery & procedures</li>
                          <li>Fertility treatments</li>
                          <li>Experimental treatments</li>
                          <li>Self-inflicted injuries</li>
                          <li>Services at non-contracted facilities</li>
                          <li>Travel health services abroad</li>
                        </ul>
                      </div>
                      <div class="col-md-6">
                        <h6 class="exclusion-title text-warning">
                          <i class="bx bx-error me-1"></i>Limitations
                        </h6>
                        <ul class="exclusion-list">
                          <li>Pre-existing conditions (waiting period may apply)</li>
                          <li>Dental care (basic only)</li>
                          <li>Eye care (excluding surgery)</li>
                          <li>Mental health (limited coverage)</li>
                          <li>Alternative/traditional medicine</li>
                          <li>Non-essential medications</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column - Summary & Actions -->
              <div class="col-xl-4">
                <!-- Coverage Summary Card -->
                <div class="card">
                  <div class="card-header bg-primary bg-gradient text-white">
                    <h5 class="card-title mb-0 text-white">
                      <i class="bx bx-bar-chart-alt-2 me-2"></i>Coverage Summary
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="summary-item">
                      <span class="summary-label">Plan Type</span>
                      <span class="summary-value">
                        <span class="badge" :class="getPlanTypeBadgeClass(planInfo.plan_type)">
                          {{ planInfo.plan_type ? capitalizeFirst(planInfo.plan_type) : 'Basic' }}
                        </span>
                      </span>
                    </div>
                    <div class="summary-item">
                      <span class="summary-label">Coverage Status</span>
                      <span class="summary-value">
                        <span class="badge" :class="profile.status === 'active' ? 'bg-success' : 'bg-danger'">
                          {{ capitalizeFirst(profile.status) }}
                        </span>
                      </span>
                    </div>
                    <div class="summary-item">
                      <span class="summary-label">Covered Members</span>
                      <span class="summary-value fw-bold">{{ totalBeneficiaries }}</span>
                    </div>
                    <div class="summary-item">
                      <span class="summary-label">Annual Premium</span>
                      <span class="summary-value fw-bold text-primary">ETB {{ formatCurrency(profile.annual_premium) }}</span>
                    </div>
                    <div class="summary-item">
                      <span class="summary-label">Amount Paid</span>
                      <span class="summary-value text-success">ETB {{ formatCurrency(totalPaidThisYear) }}</span>
                    </div>
                    <div class="summary-item" v-if="remainingBalance > 0">
                      <span class="summary-label">Balance Due</span>
                      <span class="summary-value text-danger">ETB {{ formatCurrency(remainingBalance) }}</span>
                    </div>
                    <hr>
                    <div class="summary-item">
                      <span class="summary-label">Coverage Period</span>
                      <span class="summary-value small">
                        {{ formatDate(profile.registration_date) }} - {{ formatDate(profile.expiry_date) }}
                      </span>
                    </div>
                    <div class="summary-item">
                      <span class="summary-label">Days Remaining</span>
                      <span class="summary-value fw-bold" :class="daysLeftClass">{{ daysUntilExpiry }} days</span>
                    </div>
                  </div>
                </div>

                <!-- Covered Members Card -->
                <div class="card">
                  <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-group text-primary me-2"></i>Covered Members
                    </h5>
                    <span class="badge bg-primary">{{ totalBeneficiaries }}</span>
                  </div>
                  <div class="card-body">
                    <!-- Primary Member -->
                    <div class="member-item primary-member">
                      <div class="d-flex align-items-center">
                        <img v-if="hasProfilePhoto"
                             :src="profilePictureUrl"
                             class="rounded-circle me-2"
                             style="width: 40px; height: 40px; object-fit: cover;"
                             @error="handleImageError">
                        <div v-else
                             class="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2"
                             style="width: 40px; height: 40px;">
                          <span class="text-white small">{{ getInitials(profile.full_name) }}</span>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="mb-0 small">{{ profile.full_name }}</h6>
                          <small class="text-muted">Primary Member</small>
                        </div>
                        <span class="badge bg-primary">Self</span>
                      </div>
                    </div>

                    <!-- Beneficiaries -->
                    <div v-for="beneficiary in beneficiaries" :key="beneficiary.beneficiary_id" class="member-item">
                      <div class="d-flex align-items-center">
                        <img v-if="beneficiaryHasPhoto(beneficiary)"
                             :src="getBeneficiaryPhotoUrl(beneficiary)"
                             class="rounded-circle me-2"
                             style="width: 40px; height: 40px; object-fit: cover;"
                             @error="onBeneficiaryImageError($event, beneficiary)">
                        <div v-else
                             class="bg-secondary rounded-circle d-flex align-items-center justify-content-center me-2"
                             style="width: 40px; height: 40px;">
                          <span class="text-white small">{{ getInitials(beneficiary.full_name) }}</span>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="mb-0 small">{{ beneficiary.full_name }}</h6>
                          <small class="text-muted">{{ capitalizeFirst(beneficiary.relationship) }}</small>
                        </div>
                        <span class="badge" :class="beneficiary.status === 'active' ? 'bg-success' : 'bg-warning'">
                          {{ capitalizeFirst(beneficiary.status) }}
                        </span>
                      </div>
                    </div>

                    <!-- Add More Link -->
                    <div class="text-center mt-3 d-none">
                      <router-link to="/beneficiaries-add" class="btn btn-outline-primary btn-sm">
                        <i class="bx bx-plus me-1"></i>Add Beneficiary
                      </router-link>
                    </div>
                  </div>
                </div>

                <!-- Quick Actions Card -->
                <div class="card d-none">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-grid-alt text-primary me-2"></i>Quick Actions
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="d-grid gap-2">
                      <router-link to="/find-facilities" class="btn btn-outline-primary">
                        <i class="bx bx-hospital me-2"></i>Find Health Facility
                      </router-link>
                      <router-link to="/claims-submit" class="btn btn-outline-info">
                        <i class="bx bx-file-plus me-2"></i>Submit a Claim
                      </router-link>
                      <router-link to="/claims-list" class="btn btn-outline-secondary">
                        <i class="bx bx-list-ul me-2"></i>View My Claims
                      </router-link>
                      <router-link to="/member-card" class="btn btn-outline-success">
                        <i class="bx bx-id-card me-2"></i>View Membership Card
                      </router-link>
                    </div>
                  </div>
                </div>

                <!-- Help Card -->
                <div class="card bg-light d-none">
                  <div class="card-body text-center">
                    <i class="bx bx-help-circle text-primary" style="font-size: 3rem;"></i>
                    <h6 class="mt-2">Need Help?</h6>
                    <p class="text-muted small mb-3">Have questions about your coverage or need assistance?</p>
                    <router-link to="/member-help" class="btn btn-primary btn-sm">
                      <i class="bx bx-support me-1"></i>Contact Support
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import MemberPortalNavigation from "@/components/member-portal/MemberPortalNavigation.vue";
import { memberUrl, executeJson } from "@/api";

export default {
  name: "MemberCoverage",
  components: {
    MemberPortalNavigation
  },

  data() {
    return {
      loading: false,

      // Profile data
      profile: {},
      planInfo: {},
      beneficiaries: [],

      // Stats
      beneficiaryCount: 0,
      totalPaidThisYear: 0,
      totalClaimsThisYear: 0
    };
  },

  computed: {
    // Photo URL
    profilePictureUrl() {
      if (this.profile.profile_photo) {
        if (this.profile.profile_photo.startsWith('http')) {
          return this.profile.profile_photo;
        }
        return `https://rcmsuat.craftsilicon.com/api1/uploads/member-photos/${this.profile.profile_photo}`;
      }
      return './assets/images/users/default-avatar.png';
    },

    hasProfilePhoto() {
      return this.profile && this.profile.profile_photo;
    },

    // Total beneficiaries
    totalBeneficiaries() {
      return 1 + parseInt(this.beneficiaryCount || 0);
    },

    // Days until expiry
    daysUntilExpiry() {
      if (!this.profile.expiry_date) return 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiry = new Date(this.profile.expiry_date);
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    },

    daysLeftClass() {
      if (this.daysUntilExpiry <= 30) return 'text-danger';
      if (this.daysUntilExpiry <= 60) return 'text-warning';
      return 'text-success';
    },

    // Remaining balance
    remainingBalance() {
      const annual = parseFloat(this.profile.annual_premium) || 0;
      const paid = parseFloat(this.totalPaidThisYear) || 0;
      return Math.max(0, annual - paid);
    }
  },

  mounted() {
    this.loadCoverageData();
  },

  methods: {
    // Load all coverage data
    async loadCoverageData() {
      this.loading = true;

      try {
        await Promise.all([
          this.loadMemberProfile(),
          this.loadBeneficiaries()
        ]);
      } catch (error) {
        console.error('Error loading coverage data:', error);
      } finally {
        this.loading = false;
      }
    },

    // Load member profile
    async loadMemberProfile() {
      const data = {
        function: "getMyMemberProfile"
      };

      try {
        const res = await executeJson(data, memberUrl);

        if (res.data.success) {
          this.profile = res.data.data.profile || {};
          this.planInfo = res.data.data.planInfo || {};
          this.beneficiaryCount = res.data.data.beneficiaryCount || 0;
          this.totalPaidThisYear = res.data.data.totalPaidThisYear || 0;
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    },

    // Load beneficiaries
    async loadBeneficiaries() {
      const data = {
        function: "getMyBeneficiaries"
      };

      try {
        const res = await executeJson(data, memberUrl);

        if (res.data.success) {
          this.beneficiaries = res.data.data.beneficiaries || [];
        }
      } catch (error) {
        console.error('Error loading beneficiaries:', error);
      }
    },

    // Coverage status methods
    getCoverageStatusClass() {
      if (this.profile.status !== 'active') return 'status-inactive';
      if (this.daysUntilExpiry <= 30) return 'status-warning';
      return 'status-active';
    },

    getCoverageStatusIcon() {
      if (this.profile.status !== 'active') return 'bx-x-circle';
      if (this.daysUntilExpiry <= 30) return 'bx-error-circle';
      return 'bx-check-circle';
    },

    getCoverageStatusTitle() {
      if (this.profile.status !== 'active') return 'Coverage Inactive';
      if (this.daysUntilExpiry <= 30) return 'Coverage Expiring Soon';
      return 'Coverage Active';
    },

    getCoverageStatusMessage() {
      if (this.profile.status !== 'active') {
        return 'Your CBHI coverage is currently inactive. Please contact support for assistance.';
      }
      if (this.daysUntilExpiry <= 30) {
        return `Your coverage will expire in ${this.daysUntilExpiry} days. Renew now to maintain continuous coverage.`;
      }
      return 'Your CBHI coverage is active. You can access healthcare services at any contracted facility.';
    },

    // Plan badge class
    getPlanTypeBadgeClass(planType) {
      if (!planType) return 'bg-secondary';
      const classes = {
        'basic': 'bg-secondary',
        'standard': 'bg-primary',
        'premium': 'bg-warning text-dark',
        'family': 'bg-success'
      };
      return classes[planType.toLowerCase()] || 'bg-secondary';
    },

    // Coverage usage
    getCoverageUsagePercent() {
      if (!this.planInfo.annual_coverage_limit || this.planInfo.annual_coverage_limit <= 0) return 0;
      const used = parseFloat(this.totalClaimsThisYear) || 0;
      const limit = parseFloat(this.planInfo.annual_coverage_limit);
      return Math.min(100, Math.round((used / limit) * 100));
    },

    // Image handlers
    handleImageError(event) {
      event.target.style.display = 'none';
      this.profile.profile_photo = null;
    },

    getBeneficiaryPhotoUrl(beneficiary) {
      if (beneficiary.profile_photo) {
        if (beneficiary.profile_photo.startsWith('http')) {
          return beneficiary.profile_photo;
        }
        return `https://api.craftcollect.africa/cbhi/uploads/beneficiary-photos/${beneficiary.profile_photo}`;
      }
      return null;
    },

    beneficiaryHasPhoto(beneficiary) {
      return beneficiary && beneficiary.profile_photo;
    },

    onBeneficiaryImageError(event, beneficiary) {
      event.target.style.display = 'none';
      const index = this.beneficiaries.findIndex(b => b.beneficiary_id === beneficiary.beneficiary_id);
      if (index !== -1) {
        this.beneficiaries[index].profile_photo = null;
      }
    },

    // Utility methods
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatCurrency(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat().format(amount);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
    }
  }
};
</script>

<style scoped>
/* Content area with sidebar */
.content-minimize {
  margin-left: 250px;
  transition: margin-left 0.3s ease;
}

.page-content {
  padding: 70px 0 60px 0;
  min-height: 100vh;
  background-color: #f8f9fa;
}

@media (max-width: 992px) {
  .content-minimize {
    margin-left: 0 !important;
  }
}

.vertical-collpsed .content-minimize {
  margin-left: 70px;
}

/* Coverage Status Card */
.coverage-status-card {
  border: none;
  border-radius: 1rem;
  overflow: hidden;
}

.coverage-status-card.status-active {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
}

.coverage-status-card.status-warning {
  background: linear-gradient(135deg, #f0ad4e 0%, #ffc107 100%);
}

.coverage-status-card.status-warning .coverage-icon i,
.coverage-status-card.status-warning h4,
.coverage-status-card.status-warning .text-white-75 {
  color: #1e3f70 !important;
}

.coverage-status-card.status-inactive {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
}

.coverage-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.coverage-icon i {
  font-size: 2rem;
  color: white;
}

.coverage-stat {
  background: rgba(255, 255, 255, 0.15);
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
}

.coverage-stat .stat-label {
  display: block;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.coverage-stat .stat-value {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
}

.coverage-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 1rem 1.5rem;
  border-radius: 0.75rem;
  display: inline-block;
}

.coverage-badge .badge-label {
  display: block;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  margin-bottom: 0.25rem;
}

.coverage-badge .badge-amount {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
}

.text-white-75 {
  color: rgba(255, 255, 255, 0.75) !important;
}

/* Cards */
.card {
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  margin-bottom: 1.5rem;
}

.card-header {
  background: transparent;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.25rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e3f70;
  margin: 0;
}

/* Plan Header */
.plan-name {
  color: #1e3f70;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

/* Section Subtitle */
.section-subtitle {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

/* Highlight Boxes */
.highlight-box {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.75rem;
  border: 1px solid #e9ecef;
}

.highlight-icon {
  width: 45px;
  height: 45px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  font-size: 1.25rem;
}

.highlight-content {
  flex: 1;
}

.highlight-label {
  display: block;
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.highlight-value {
  display: block;
  font-size: 1rem;
  font-weight: 600;
  color: #1e3f70;
}

/* Soft Background Colors */
.bg-soft-primary {
  background-color: rgba(44, 90, 160, 0.15);
}

.bg-soft-info {
  background-color: rgba(23, 162, 184, 0.15);
}

.bg-soft-success {
  background-color: rgba(40, 167, 69, 0.15);
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.15);
}

.bg-soft-danger {
  background-color: rgba(220, 53, 69, 0.15);
}

.bg-soft-secondary {
  background-color: rgba(108, 117, 125, 0.15);
}

/* Limit Card */
.limit-card {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  border: 1px solid #e9ecef;
}

.limit-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.limit-value {
  font-size: 1.1rem;
  font-weight: 700;
}

/* Services Grid */
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
}

.service-category {
  background: #f8f9fa;
  border-radius: 0.75rem;
  padding: 1.25rem;
  border: 1px solid #e9ecef;
}

.category-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.category-icon {
  width: 40px;
  height: 40px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.75rem;
  font-size: 1.25rem;
}

.category-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e3f70;
  margin: 0;
}

.service-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.service-list li {
  display: flex;
  align-items: center;
  padding: 0.35rem 0;
  font-size: 0.85rem;
  color: #495057;
}

.service-list li i {
  margin-right: 0.5rem;
  font-size: 1rem;
}

/* Exclusions */
.exclusion-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
}

.exclusion-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.exclusion-list li {
  padding: 0.35rem 0;
  font-size: 0.85rem;
  color: #6c757d;
  padding-left: 1rem;
  position: relative;
}

.exclusion-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #6c757d;
}

/* Summary Items */
.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e9ecef;
}

.summary-item:last-child {
  border-bottom: none;
}

.summary-label {
  font-size: 0.875rem;
  color: #6c757d;
}

.summary-value {
  font-size: 0.875rem;
}

/* Member Items */
.member-item {
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  background: #f8f9fa;
  transition: all 0.2s ease;
}

.member-item:hover {
  background: #e9ecef;
}

.member-item.primary-member {
  background: rgba(44, 90, 160, 0.1);
  border: 1px solid rgba(44, 90, 160, 0.2);
}

/* Responsive */
@media (max-width: 768px) {
  .coverage-status-card .card-body {
    padding: 1rem;
  }

  .coverage-stat {
    padding: 0.35rem 0.75rem;
    font-size: 0.8rem;
  }

  .coverage-badge {
    padding: 0.75rem 1rem;
  }

  .coverage-badge .badge-amount {
    font-size: 1.25rem;
  }

  .services-grid {
    grid-template-columns: 1fr;
  }

  .highlight-box {
    padding: 0.75rem;
  }

  .highlight-icon {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
}
</style>